/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { tracked } from '@glimmer/tracking';
import InAppMessage from 'cio/app-models/in-app/in-app-message';
import Composer, { BaseComposerArgs } from 'cio/components/action-composer/base-glimmer';
import { InAppPreview } from 'cio/utils/previews';
import { action } from '@ember/object';
import { Editor } from 'cio/services/editor';
import { isBlank } from '@ember/utils';
import { DEFAULT_RECIPIENT, inAppMessageDisplayLabel } from 'cio/utils/in-app/message-config';
import { hasInAppPreviewErrors } from 'cio/utils/in-app/preview-error';
import { getMessageWithDefaultsForPreview } from 'cio/utils/in-app/preview';
import { Framecast } from 'framecast';
import { inject as service } from '@ember/service';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import { TrackedEvents } from 'cio/utils/in-app/events';

interface ComposerArgs extends BaseComposerArgs {
  preview: InAppPreview;
  previewVariables: Record<string, unknown>;
  hasReadOnlyPermission: boolean;
}

interface ParcelSignature {
  Args: ComposerArgs;
  Element: HTMLDivElement;
}

export default class Parcel extends Composer<ParcelSignature> {
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;

  @tracked message!: InAppMessage;
  @tracked framecast: Framecast | null = null;

  parsedTemplateId?: string;
  previewObserverKeys = [];
  previewURL = 'previews/in_app';

  constructor(owner: unknown, args: ParcelSignature['Args']) {
    super(owner, args);
    this.deserializeMessage();
    this.watchTemplate();
  }

  get isInvalid() {
    return (
      this.isRecipientInvalid ||
      this.message.hasConfigErrors ||
      this.message.hasAttributeErrors ||
      this.hasPreviewErrors
    );
  }

  get isRecipientInvalid() {
    return isBlank(this.template.recipient);
  }

  get hasPreviewErrors() {
    return hasInAppPreviewErrors(this.args.preview);
  }

  get isDirty() {
    return this.template.get('hasDirtyAttributes') || this.message.isDirty;
  }

  @action
  async generateHTML() {
    try {
      this.template.body = (await this.framecast?.call('getOutput')) ?? '';
    } catch (e) {
      this.alerts.error('parcel.notification', 'Error generating HTML');
    }
  }

  @action
  attachFramecast(iframe: HTMLIFrameElement) {
    this.framecast = new Framecast(iframe.contentWindow as Window);
  }

  @action
  onMessageAttributeChange(name: string, value: string) {
    this.message.setAttribute(name, value);
  }

  @action
  onMessageConfigChange(config: InAppMessage) {
    let { expiration, priority, display, routeRules, persistent } = config;
    this.message.configure({ expiration, priority, display, routeRules, persistent });
  }

  @action
  onMessageCartaContentChange(content: string) {
    this.message.changeCartaContent(content);
  }

  @action
  onMessageTemplateChange(messageId: string, attributes: string[]) {
    this.message.changeMessage(messageId, attributes);
  }

  @action
  onRecipientChange(recipient: string) {
    this.template.recipient = recipient;
  }

  @action
  onTemplateChange(id: string) {
    if (id !== this.parsedTemplateId) {
      this.deserializeMessage();
    }
  }

  @action
  async save() {
    await this.prepareSave();
    await this.args.onSave();
    this.deserializeMessage();
    this.trackEditCompleted();
    this.resetDirtyState();
  }

  @action
  rollback() {
    this.template.rollbackAttributes();
    this.deserializeMessage();
    this.resetDirtyState();
    this.args.onRollback();
  }

  @action
  onDirtyChange(_element: HTMLElement, [isDirty]: [boolean]) {
    this.args.onDirtyChange?.(isDirty);
  }

  getPreviewData() {
    return {
      to: this.template.recipient,
      body_json: getMessageWithDefaultsForPreview(this.message).serialize(),
      body_html: this.template.body,
      editor: Editor.VISUAL,
    };
  }

  private trackEditCompleted() {
    let to = this.template.recipient as string;
    let editor = this.template.editor as Editor;

    let { persistent, attributes, expiration, display, priority, routeRules } = this.message;
    let position = inAppMessageDisplayLabel(display);
    this.analytics.track('in_app', TrackedEvents.EditCompleted, {
      to,
      attributes,
      expiration,
      position,
      priority,
      routeRules,
      persistent,
      editor,
    });
  }

  private resetDirtyState() {
    this.args.onDirtyChange?.(false);
  }

  private async prepareSave() {
    let { template } = this;
    template.editor = Editor.VISUAL;
    template.bodyJson = this.message.serialize();
    await this.generateHTML();
    this.setDefaultRecipient();
  }

  private setDefaultRecipient() {
    let { template } = this;
    if (isBlank(template.recipient)) {
      template.recipient = DEFAULT_RECIPIENT;
    }
  }

  private deserializeMessage() {
    this.message = InAppMessage.deserialize(this.template.bodyJson);
    this.parsedTemplateId = this.template.id;
  }
}
